import React, { useEffect, useState, useMemo } from 'react';
import { useMeasure } from 'react-use';
import classNames from 'classnames';
import { getHighLightedDate, getInitialDatesArray } from '../utils/index';
import {
  getNewIndex,
  findSelectedDietIndex,
  getNewFetchDate,
} from '../helpers/menuHelpers';
import { CustomModal } from './CustomModal';
import MenuTitle from './MenuTitle';
import Footer from './Footer';
import Loader from './Loader';
import TopBarModal from './TopBarModal';
import NoDiets from './NoDiets';
import ChooseDietModal from './ChooseDietModal';
import BarWithSelectOnDesktop from './BarWithSelectOnDesktop';
import BarWithSelectOnMobile from './BarWithSelectOnMobile';
import Carousel from './Carousel';
import { fetchMenus } from '../helpers/fetchMenus';
import { separateDietIdAndTierIdsByUnderscore } from '../helpers/mergeDietIdAndTierId.util';

export const MenuWidget = ({
  companyDietDetails,
  companyId,
  companyParams: { menuVisibleInDietly },
  initDietId,
  path,
  showSelect,
}) => {
  const [ref, { width }] = useMeasure();
  const isWide = useMemo(() => width >= 768, [width]);

  const [dates, setDates] = useState(getInitialDatesArray);
  const [highlightedDate, setHighLightedDate] = useState(getHighLightedDate);

  const [selectedDiet, selectDiet] = useState({});
  const [menus, setMenus] = useState({});

  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleSelectDiet = diet => {
    setAndFetchDietData(diet);
    setIsModalOpen(false);
  };

  const getNextDiet = (direction, currentIndex, length) => {
    const nextDietIndex =
      getNewIndex(direction, currentIndex, length ?? 0) ?? 0;
    return companyDietDetails[nextDietIndex] ?? {};
  };

  const handleSelectNextDiet = direction => {
    setAndFetchDietData(
      getNextDiet(
        direction,
        findSelectedDietIndex(selectedDiet, companyDietDetails),
        companyDietDetails?.length
      )
    );
  };

  useEffect(() => {
    const setInitDietAndMenu = async () => {
      if (initDietId && companyDietDetails.length > 0) {
        const initDiet =
          companyDietDetails.find(diet => {
            const { dietId } =
              separateDietIdAndTierIdsByUnderscore(diet?.dietIdWithTierId) ||
              {};

            return dietId === initDietId;
          }) || companyDietDetails[0];

        const menu = await fetchMenus(
          companyId,
          initDiet.dietIdWithTierId,
          getInitialDatesArray()
        );

        selectDiet(initDiet);
        setMenus(menu || {});
      }
    };

    void setInitDietAndMenu();
  }, [companyDietDetails, companyId, initDietId]);

  //------------------------------------ Fetches -------------------------------------------
  const fetchInitialMenu = async (selectedDiet, dates, cb) => {
    cb && cb(true);

    try {
      const menu = await fetchMenus(
        companyId,
        selectedDiet.dietIdWithTierId,
        dates
      );
      setMenus(menu);
      cb && cb(false);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
      cb && cb(false);
    }
  };

  const fetchSingleMenu = async (selectedDiet, dates) => {
    try {
      const menu = await fetchMenus(
        companyId,
        selectedDiet.dietIdWithTierId,
        dates
      );

      setMenus(menus => ({
        ...menus,
        ...menu,
      }));
    } catch (error) {
      console.log(error);
    }
  };

  const setAndFetchDietData = diet => {
    const dates = getInitialDatesArray();

    void fetchInitialMenu(diet, dates, setIsLoading);
    setDates(dates);
    selectDiet(diet);
    setHighLightedDate(getHighLightedDate());
  };

  const fetchMenu = () => {
    void fetchSingleMenu(selectedDiet, [getNewFetchDate('up', dates)]);
  };

  return (
    <>
      <CustomModal
        className="menu-widget__custom-modal dietly-app-modal-1 dietly-app-modal-2 dietly-app-modal-3 dietly-app-modal-4"
        isOpen={isModalOpen}
        overlayClassName="menu-widget__custom-modal-overlay"
      >
        <TopBarModal closeModal={handleCloseModal} path={path} />

        <ChooseDietModal
          companyDietDetails={companyDietDetails}
          path={path}
          selectDiet={handleSelectDiet}
          selectedDietId={selectedDiet?.dietIdWithTierId}
        />
      </CustomModal>

      <div
        className={classNames({
          'menu-widget-catering-menu': true,
          'widget-paddings__mobile': !isWide,
          'widget-paddings__desktop': isWide,
        })}
        ref={ref}
      >
        <MenuTitle />

        {isWide ? (
          <BarWithSelectOnDesktop
            companyDietDetails={companyDietDetails}
            path={path}
            selectedDiet={selectedDiet}
            selectDiet={handleSelectDiet}
            selectNextDiet={handleSelectNextDiet}
            showSelect={showSelect}
          />
        ) : (
          <BarWithSelectOnMobile
            openModal={handleOpenModal}
            selectedDiet={selectedDiet}
            showSelect={showSelect}
          />
        )}

        {menuVisibleInDietly ? (
          <div>
            {isLoading ? (
              <Loader path={path} />
            ) : (
              <Carousel
                dates={dates}
                fetchMenu={fetchMenu}
                highlightedDate={highlightedDate}
                isWide={isWide}
                menus={menus}
                path={path}
                setDates={setDates}
                setHighLightedDate={setHighLightedDate}
              />
            )}
          </div>
        ) : (
          <NoDiets />
        )}

        <Footer />
      </div>
    </>
  );
};
